// @ts-nocheck
import { useRecruitmentRepository, useOnboardingRepository } from '../repositories';
import { useCompatQuery, useCompatMutation } from './useApolloCompat';
import {
  JOB_POSTINGS_QUERY,
  UPDATE_JOB_POSTING_MUTATION,
  CLOSE_JOB_POSTING_MUTATION,
  APPLICANTS_QUERY,
  APPLICANT_QUERY,
  CREATE_APPLICANT_MUTATION,
  UPDATE_APPLICANT_STATUS_MUTATION,
  ADD_APPLICANT_NOTE_MUTATION,
  RATE_APPLICANT_MUTATION,
  INTERVIEWS_QUERY,
  SCHEDULE_INTERVIEW_MUTATION,
  CANCEL_INTERVIEW_MUTATION,
  SUBMIT_INTERVIEW_FEEDBACK_MUTATION,
  ONBOARDING_CHECKLISTS_QUERY,
  ONBOARDING_CHECKLIST_QUERY,
  CREATE_ONBOARDING_CHECKLIST_MUTATION,
  COMPLETE_ONBOARDING_ITEM_MUTATION,
  SKIP_ONBOARDING_ITEM_MUTATION,
  ADD_ONBOARDING_ITEM_MUTATION,
} from '../graphql/operations';
import type {
  JobPosting,
  Applicant,
  Interview,
  OnboardingChecklist,
  CreateJobPostingInput,
  UpdateJobPostingInput,
  CreateApplicantInput,
  ApplicantStatus,
  ScheduleInterviewInput,
  InterviewFeedbackInput,
  CreateOnboardingChecklistInput,
  AddOnboardingItemInput,
} from '../types/recruitment';

// =====================================================================
// HOOKS -- Job Postings
// =====================================================================

/** List job postings (via Repository) */
export function useJobPostings(variables?: { status?: string; department?: string }) {
  const repo = useRecruitmentRepository();
  return repo.useList(variables);
}

/** Get a single job posting (via Repository) */
export function useJobPosting(id: string) {
  const repo = useRecruitmentRepository();
  return repo.useGet(id || null);
}

/** Create a job posting (via Repository) */
export function useCreateJobPosting() {
  const repo = useRecruitmentRepository();
  return repo.useCreate();
}

/** Update a job posting (specialized -- uses fragment-based mutation) */
export function useUpdateJobPosting() {
  return useCompatMutation(UPDATE_JOB_POSTING_MUTATION, {
    refetchQueries: [{ query: JOB_POSTINGS_QUERY }],
  });
}

/** Close a job posting (specialized pipeline action) */
export function useCloseJobPosting() {
  return useCompatMutation(CLOSE_JOB_POSTING_MUTATION, {
    refetchQueries: [{ query: JOB_POSTINGS_QUERY }],
  });
}

// =====================================================================
// HOOKS -- Applicants
// =====================================================================

export function useApplicants(variables?: { jobPostingId?: string; status?: string }) {
  return useCompatQuery(APPLICANTS_QUERY, { variables });
}

export function useApplicant(id: string) {
  return useCompatQuery(APPLICANT_QUERY, {
    variables: { id },
    skip: !id,
  });
}

export function useCreateApplicant() {
  return useCompatMutation(CREATE_APPLICANT_MUTATION, {
    refetchQueries: [
      { query: APPLICANTS_QUERY },
      { query: JOB_POSTINGS_QUERY },
    ],
  });
}

/** Update applicant status (specialized pipeline action) */
export function useUpdateApplicantStatus() {
  return useCompatMutation(UPDATE_APPLICANT_STATUS_MUTATION, {
    refetchQueries: [
      { query: APPLICANTS_QUERY },
      { query: JOB_POSTINGS_QUERY },
    ],
  });
}

/** Add a note to an applicant (specialized pipeline action) */
export function useAddApplicantNote() {
  return useCompatMutation(ADD_APPLICANT_NOTE_MUTATION);
}

/** Rate an applicant (specialized pipeline action) */
export function useRateApplicant() {
  return useCompatMutation(RATE_APPLICANT_MUTATION, {
    refetchQueries: [{ query: APPLICANTS_QUERY }],
  });
}

// =====================================================================
// HOOKS -- Interviews
// =====================================================================

export function useInterviews(variables?: { applicantId?: string }) {
  return useCompatQuery(INTERVIEWS_QUERY, { variables });
}

/** Schedule an interview (specialized pipeline action) */
export function useScheduleInterview() {
  return useCompatMutation(SCHEDULE_INTERVIEW_MUTATION, {
    refetchQueries: [
      { query: INTERVIEWS_QUERY },
      { query: APPLICANTS_QUERY },
    ],
  });
}

/** Cancel an interview (specialized pipeline action) */
export function useCancelInterview() {
  return useCompatMutation(CANCEL_INTERVIEW_MUTATION, {
    refetchQueries: [{ query: INTERVIEWS_QUERY }],
  });
}

/** Submit interview feedback (specialized pipeline action) */
export function useSubmitInterviewFeedback() {
  return useCompatMutation(SUBMIT_INTERVIEW_FEEDBACK_MUTATION, {
    refetchQueries: [
      { query: INTERVIEWS_QUERY },
      { query: APPLICANTS_QUERY },
    ],
  });
}

// =====================================================================
// HOOKS -- Onboarding
// =====================================================================

/** List onboarding checklists (via Repository) */
export function useOnboardingChecklists(variables?: { employeeId?: string }) {
  const repo = useOnboardingRepository();
  return repo.useList(variables);
}

/** Get a single onboarding checklist (specialized -- needs dedicated query) */
export function useOnboardingChecklist(id: string) {
  return useCompatQuery(ONBOARDING_CHECKLIST_QUERY, {
    variables: { id },
    skip: !id,
  });
}

/** Create an onboarding checklist (specialized) */
export function useCreateOnboardingChecklist() {
  return useCompatMutation(CREATE_ONBOARDING_CHECKLIST_MUTATION, {
    refetchQueries: [{ query: ONBOARDING_CHECKLISTS_QUERY }],
  });
}

/** Complete an onboarding item (specialized pipeline action) */
export function useCompleteOnboardingItem() {
  return useCompatMutation(COMPLETE_ONBOARDING_ITEM_MUTATION, {
    refetchQueries: [{ query: ONBOARDING_CHECKLISTS_QUERY }],
  });
}

/** Skip an onboarding item (specialized pipeline action) */
export function useSkipOnboardingItem() {
  return useCompatMutation(SKIP_ONBOARDING_ITEM_MUTATION, {
    refetchQueries: [{ query: ONBOARDING_CHECKLISTS_QUERY }],
  });
}

/** Add an onboarding item (specialized pipeline action) */
export function useAddOnboardingItem() {
  return useCompatMutation(ADD_ONBOARDING_ITEM_MUTATION, {
    refetchQueries: [{ query: ONBOARDING_CHECKLISTS_QUERY }],
  });
}
